import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import moment from 'moment';
import React from 'react';
import _ from 'underscore';

import {UpdateBenchlingAppDefinitionVersionArgs} from 'benchling-apps/BenchlingAppDAO';
import BenchlingConfig from 'BenchlingConfig';
import {PublicationState} from 'graphql-client/__generated/graphqlGlobalTypes';
import Label from 'ui-library/bs/Label';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Pill, {PillStyle} from 'ui-library/buttons/Pill';
import * as Colors from 'ui-library/Colors';
import EditableTextField, {TextFieldType} from 'ui-library/inputs/EditableTextField';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import legacyRunModal from 'ui-library/legacyRunModal';
import {Tab, TabContainer} from 'ui-library/TabContainer';

import {BenchlingAppSettingsRow} from './BenchlingAppOverview';
import {AppDefinition, AppDefinitionVersion} from './BenchlingAppsPage';
import {DATE_FORMAT} from './BenchlingAppVersionHistory';
import InstallOnHomeTenantModal from './InstallOnHomeTenantModal';
import ManageAllowList from './ManageAllowList';

type VersionAllowList = AppDefinitionVersion['allowList'];

const VERSION_BASIC_INFORMATION = 'VERSION_BASIC_INFORMATION';

interface VersionPaneProps {
  appDefinition: AppDefinition;
  version: AppDefinitionVersion;
  handleSubmitDefinitionVersion: (newValue: UpdateBenchlingAppDefinitionVersionArgs) => void;
  refetchAppDefinition: () => void;
}

export function BenchlingAppVersionPane({
  appDefinition,
  version,
  handleSubmitDefinitionVersion,
  refetchAppDefinition,
}: VersionPaneProps): JSX.Element {
  const isPublished = version.publicationState !== PublicationState.PRIVATE;

  return (
    <>
      <VersionPaneHeader
        isPublished={isPublished}
        appDefinitionId={appDefinition.id}
        currentlyInstalledVersion={appDefinition.installation?.versionNumber || null}
        version={version}
        allowList={version.allowList}
        handleSubmitDefinitionVersion={handleSubmitDefinitionVersion}
        refetchAppDefinition={refetchAppDefinition}
      />
      <TabContainer
        activeTab={VERSION_BASIC_INFORMATION}
        paneClassName="u-flex-grow"
        className={classNames(css(styles.infoPane), 'u-flex-grow')}
        onRequestTabChange={() => {}}
      >
        <Tab tabKey={VERSION_BASIC_INFORMATION} title="Basic Information">
          <BasicInfoPane
            appDefinition={appDefinition}
            version={version}
            isPublished={isPublished}
            handleSubmitDefinitionVersion={handleSubmitDefinitionVersion}
          />
        </Tab>
      </TabContainer>
      <VersionPaneFooter version={version} />
    </>
  );
}

interface VersionPaneHeaderProps {
  appDefinitionId: string;
  currentlyInstalledVersion: string | null;
  version: AppDefinitionVersion;
  isPublished: boolean;
  allowList: VersionAllowList;
  handleSubmitDefinitionVersion: (newValue: UpdateBenchlingAppDefinitionVersionArgs) => void;
  refetchAppDefinition: () => void;
}

function getAppStatusPill(isPublished: boolean): JSX.Element {
  const icon = isPublished ? 'check-circle' : 'clock-rotate-left';
  const label = isPublished ? 'Published' : 'Draft';
  const style = isPublished ? PillStyle.SUCCESS : PillStyle.SECONDARY;
  return <Pill icon={icon} text={label} style={style} />;
}

function VersionPaneHeader(props: VersionPaneHeaderProps): JSX.Element {
  const sharingDisabledMsg = props.version.shareDisabledReason
    ? `Cannot share app version: ${props.version.shareDisabledReason}`
    : null;

  return (
    <div className={classNames(css(styles.paneHeader))}>
      <div className={classNames(css(styles.paneHeaderLeft))}>{getAppStatusPill(props.isPublished)}</div>
      <div className={classNames(css(styles.paneHeaderRight))}>
        {props.isPublished && (
          <div className={classNames(css(styles.lockedWarning))}>
            Published versions are locked from editing
          </div>
        )}
        {props.appDefinitionId != null && (
          <div className="u-lr-split">
            <div>
              {BenchlingConfig.ENABLE_BENCHLING_APPS_CREATE_INSTALL_SEPARATION ? (
                <InstallVersionButton {...props} />
              ) : (
                <></>
              )}
              <TooltipWrapper tooltipTitle={props.version.shareDisabledReason ? sharingDisabledMsg : null}>
                <Button
                  buttonStyle={ButtonStyle.SECONDARY}
                  size={ButtonSize.SMALL}
                  text="Share"
                  disabled={props.version.shareDisabledReason != null}
                  dataTestAttrs={{component: 'BenchlingAppListing', element: 'shareButton'}}
                  onClick={() => {
                    legacyRunModal(ManageAllowList, {
                      appDefinitionId: props.appDefinitionId,
                      allowList: props.allowList,
                      handleSubmitDefinitionVersion: props.handleSubmitDefinitionVersion,
                    });
                  }}
                />
              </TooltipWrapper>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function VersionPaneFooter({version}: {version: AppDefinitionVersion | null}): JSX.Element {
  const created = version?.createdAt;
  const modified = version?.modifiedAt;
  const formatCreated = created ? moment(new Date(created)).format(DATE_FORMAT) : null;
  const formatModified = modified ? moment(new Date(modified)).format(DATE_FORMAT) : null;

  return (
    <div className={classNames(css(styles.border, styles.footer))}>
      <BenchlingAppSettingsRow className={css(styles.bottomless)}>
        {version?.createdAt ? (
          <div>
            <Label className={classNames(css(styles.spaceRight))}>Created on:</Label>
            <Label className={classNames(css(styles.footerText))}>{formatCreated} </Label>
          </div>
        ) : null}
        {version?.modifiedAt ? (
          <div>
            <Label className={classNames(css(styles.spaceRight))}>Modified:</Label>
            <Label className={classNames(css(styles.footerText))}>{formatModified}</Label>
          </div>
        ) : null}
      </BenchlingAppSettingsRow>
    </div>
  );
}

function InstallVersionButton({
  appDefinitionId,
  currentlyInstalledVersion,
  version,
  refetchAppDefinition,
}: {
  appDefinitionId: string;
  currentlyInstalledVersion: string | null;
  version: AppDefinitionVersion;
  refetchAppDefinition: () => void;
}): JSX.Element {
  const versionAlreadyInstalled = currentlyInstalledVersion === version.versionId;
  return (
    <TooltipWrapper
      tooltipTitle={
        versionAlreadyInstalled ? 'This version is already installed on the current tenant.' : null
      }
    >
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        size={ButtonSize.SMALL}
        text="Install"
        disabled={versionAlreadyInstalled}
        className={classNames(css(styles.installButton))}
        onClick={() => {
          legacyRunModal(InstallOnHomeTenantModal, {
            version,
            appDefinitionId,
            currentlyInstalledVersion,
            refetchAppDefinition,
          });
        }}
      />
    </TooltipWrapper>
  );
}

export function BasicInfoPane({
  appDefinition,
  version,
  isPublished,
  handleSubmitDefinitionVersion,
}: {
  appDefinition: AppDefinition;
  version: AppDefinitionVersion;
  isPublished: boolean;
  handleSubmitDefinitionVersion: (newValue: UpdateBenchlingAppDefinitionVersionArgs) => void;
}): JSX.Element {
  const {name, description} = version;
  const currentUpdateShape = description ? {name, description} : {name};

  function getVersionLockedTooltip(): string | null {
    if (version == null) {
      return null;
    }
    if (isPublished) {
      return 'This field cannot be edited because the current app version has been published';
    } else if (
      !BenchlingConfig.ENABLE_BENCHLING_APPS_CREATE_INSTALL_SEPARATION &&
      appDefinition.installation &&
      appDefinition.installation.versionNumber !== version.versionId
    ) {
      return `Visit version ${appDefinition.installation.versionNumber} of this app to make changes to this field.`;
    } else {
      return null;
    }
  }

  return (
    <>
      <InputWrapper
        className={classNames('u-flex-grow', 'simpleton')}
        id={_.uniqueId('benchlingAppDetail')}
        label="Version"
        labelStyle={FieldLabel.STYLES.LABEL}
      >
        <EditableTextField
          isFullWidth={false}
          isDisabled={true}
          requestChange={() => {}}
          type={TextFieldType.TEXT}
          value={version.versionId}
          dataTestAttrs={{component: 'EditableTextField', element: 'version'}}
        />
      </InputWrapper>

      <InputWrapper id={_.uniqueId('benchlingAppDetail')} label="Name" labelStyle={FieldLabel.STYLES.LABEL}>
        {/* TODO: (BNCH-77887) Use Save button instead of EditTextField */}
        <EditableTextField
          inputClassName={classNames(css(styles.textBox))}
          disableEditsWithTooltip={getVersionLockedTooltip()}
          requestChange={(newName: string) => {
            handleSubmitDefinitionVersion({...currentUpdateShape, name: newName});
          }}
          type={TextFieldType.TEXT}
          value={version.name}
          dataTestAttrs={{component: 'EditableTextField', element: 'name'}}
        />
      </InputWrapper>

      <InputWrapper
        id={_.uniqueId('benchlingAppDetail')}
        label="Description"
        labelStyle={FieldLabel.STYLES.LABEL}
      >
        {' '}
        {/* TODO: (BNCH-77887) Use Save button instead of EditTextField */}
        <EditableTextField
          inputClassName={classNames(css(styles.textBox))}
          disableEditsWithTooltip={getVersionLockedTooltip()}
          emptyValueAllowed={true}
          requestChange={(newDescription: string) => {
            handleSubmitDefinitionVersion({...currentUpdateShape, description: newDescription});
          }}
          type={TextFieldType.TEXTAREA}
          value={description ?? ''}
          dataTestAttrs={{component: 'EditableTextField', element: 'description'}}
        />
      </InputWrapper>
    </>
  );
}

const styles = StyleSheet.create({
  paneHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 24,
    paddingBottom: 24,
    minHeight: '2em',
  },
  paneHeaderLeft: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  paneHeaderRight: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  textBox: {
    minWidth: '250px',
  },
  spaceRight: {
    marginRight: '.4em',
  },
  infoPane: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '70em',
    minHeight: '30em',
  },
  footer: {
    marginLeft: -16,
    marginRight: -16,
    marginBottom: -1,
  },
  footerText: {
    fontWeight: 'normal',
  },
  border: {
    border: 'solid 1px',
    borderColor: Colors.GRAY,
    paddingTop: 8,
    paddingBottom: 8,
    paddingRight: 16,
    paddingLeft: 16,
  },
  bottomless: {
    marginBottom: 0,
  },
  lockedWarning: {
    fontStyle: 'italic',
    marginRight: '15px',
    color: Colors.GRAY_DARK,
  },
  publicKeyWarningBanner: {
    marginRight: '10px',
    marginBottom: '10px',
  },
  installButton: {
    marginRight: '10px',
  },
});
